// TSL Block Studio gate helpers. Bridges the editor (text or visual → AST) to the validator + the full
// server-equivalent gate. The same code runs in the browser (live validation, client preview) and is the
// authoritative gate the edge function re-runs at publish. AI-generated TSL flows through gateStudioText —
// it is PARSED + GATED before use; it can never bypass the parser into host code.

import { parseBlock, type BlockParseResult } from './blockText';
import { validateTslBlock, TSL_LIMITS, type TslValidation } from './validate';
import { runTslGate } from './runTslGate';
import type { TslBlock } from './ast';
import type { GateReport } from '../../types/customBlockLibrary';

export interface BudgetUsage { nodes: number; maxNodes: number; lookback: number; maxLookback: number; state: number; maxState: number; params: number; maxParams: number; }
const budget = (b: TslBlock, v: TslValidation): BudgetUsage => ({
  nodes: v.nodes, maxNodes: TSL_LIMITS.maxNodes, lookback: v.lookback, maxLookback: TSL_LIMITS.maxLookback,
  state: Object.keys(b.state ?? {}).length, maxState: TSL_LIMITS.maxState, params: Object.keys(b.params ?? {}).length, maxParams: TSL_LIMITS.maxParams,
});

export interface StudioValidation { ok: boolean; parseErrors: Array<{ line: number; error: string }>; validateErrors: string[]; budget: BudgetUsage | null; block: TslBlock | null; }

/** Live (cheap) validation for the editor: parse text → structural/type/budget validate. No interpreter. */
export function validateStudioText(text: string): StudioValidation {
  const p: BlockParseResult = parseBlock(text);
  if (!p.ok) return { ok: false, parseErrors: p.errors, validateErrors: [], budget: null, block: null };
  return validateStudioBlock(p.block);
}
export function validateStudioBlock(block: TslBlock): StudioValidation {
  const v = validateTslBlock(block);
  return { ok: v.ok, parseErrors: [], validateErrors: v.errors, budget: budget(block, v), block };
}

/** Full server-equivalent gate (publish-time): validate + determinism/no-lookahead/sizing/risk via the
 *  interpreter. Publishing stays disabled until this passes. */
export async function gateStudioBlock(block: TslBlock): Promise<{ report: GateReport }> {
  return { report: await runTslGate(block) };
}
export async function gateStudioText(text: string): Promise<{ ok: boolean; errors?: Array<{ line: number; error: string }>; block?: TslBlock; report?: GateReport }> {
  const p = parseBlock(text);
  if (!p.ok) return { ok: false, errors: p.errors };               // AI/text JS is rejected HERE, before gating
  const report = await runTslGate(p.block);
  return { ok: report.publicVisible.passed, block: p.block, report };
}
